/* eslint-disable padding-line-between-statements */
import { Loader } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ServiceManager } from '@jutro/legacy/services';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { PhoneUtil } from '@xengage/gw-portals-viewmodel-js';
import {
    ViewModelForm,
    withViewModelService
} from '@xengage/gw-portals-viewmodel-react';
import { ContactService } from 'gw-capability-gateway';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import messages from './CustomContactDetails.messages';
import metadata from './CustomContactDetails.metadata.json5';
import styles from './CustomContactDetails.module.scss';

import { Link } from '@jutro/router';

function CustomContactDetailsPage(props) {
    const { location, authHeader, viewModelService } =props;
    const [accountContactDetailsVM, setAccountContactDetailsVM] = useState({});
    const [showLoadingIndicator, setShowLoadingIndicator] = useState(true);
    const contactId = location.state.contactId;
    const accountOrPolicyNumber = location.state.accountOrPolicyNumber;
    const contact = location.state.contact;
    const relatedTo = location.state.relatedTo;

    const translator = useContext(TranslatorContext);

    useEffect(() => {
        getAccountContacts();
    }, []);

    const getAccountContacts = useCallback(async() => {
        let getContactDetailsPromise;

        if (contact !== 'policyContacts') {
            getContactDetailsPromise = await ContactService.getAccountContactDetails(contactId, authHeader);
        } else {
            getContactDetailsPromise = await ContactService.getPolicyContactDetails(
                contactId,
                accountOrPolicyNumber,
                authHeader
            );
        }
        createContactVM(getContactDetailsPromise);
        setShowLoadingIndicator(false);
    },[]);

    const createContactVM = useCallback((response) =>{
        const contactRole = relatedTo === 'policies'
        ? 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO'
        : 'edge.capabilities.policycommon.accountcontact.dto.AccountContactDTO';  //AccountContactDetailsDTO wasn't getting generated in product-metadata
        const contactVM = viewModelService.create(response, 'pc', contactRole);
        
        setAccountContactDetailsVM(contactVM.value);
    },[viewModelService]);

    const getAssociatedAccountsIcon = () => {
        const accountsList =  accountContactDetailsVM.associatedAccounts;       

        const overrides =
            accountsList &&
            accountsList.map((account, index) => {
                return {
                    [`associatedAccounts${index}`]: {
                        to: `/accounts/${account.accountNumber}/summary`,
                        content: `${account.accountHolderName} (${account.accountNumber})`,
                    },
                    [`associatedAccountsIcon${index}`]: {
                        icon:
                            account.subtype === 'Company'
                                ? 'business'
                                : 'person',
                    },
                };
            });
        return Object.assign({}, ...overrides);
    };

    const getWorkPhoneNumber = workNumber => {
        if (_.isNil(workNumber)) {
            return null;
        }
        const localeService = ServiceManager.getService('locale-service');
        const phoneUtil = PhoneUtil();
        return phoneUtil.prettyPrint(
            workNumber,
            localeService.getDefaultCountryCode()
        );
    };

    if (showLoadingIndicator) {
        return <Loader loaded={!showLoadingIndicator} />;
    }

    const getPrimaryPhoneNumber = () => {
        let primaryPhoneType = '';
        const code = _.get(accountContactDetailsVM, 'primaryPhoneType');
        switch (code) {
            case 'work':
                primaryPhoneType = 'workNumber';
                break;
            case 'home':
                primaryPhoneType = 'homeNumber';
                break;
            case 'mobile':
                primaryPhoneType = 'cellNumber';
                break;
            default:
                break;
        }
        return primaryPhoneType;
    };
    
    const getPhoneLabel = () => {
        let label = '';
        const code = _.get(accountContactDetailsVM, 'primaryPhoneType');
        switch (code) {
            case 'work':
                label = (translator(messages.workPhonePrimary))
                break;
            case 'home':
                label = (translator(messages.homePhonePrimary))
                break;
            case 'mobile':
                label = (translator(messages.mobilePrimary))
                break;
            default:
                break;
        }
        return label;
    }
    
    const isPrimaryPhoneNumberVisible = () => (_.get(accountContactDetailsVM, 'homeNumber') !== undefined && _.get(accountContactDetailsVM, 'homeNumber') !== '')
            || (_.get(accountContactDetailsVM, 'workNumber') !== undefined && _.get(accountContactDetailsVM, 'workNumber') !== '')
            || (_.get(accountContactDetailsVM, 'cellNumber') !== undefined &&  _.get(accountContactDetailsVM, 'cellNumber') !== '')

    const overrideProps = {
        '@field': {
            readOnly: true,
        },
        contactDetailsContainer: {
            visible: !showLoadingIndicator,
        },
        policySourceState: {
            visible: contact === 'policyContacts',
            to: {
                pathname: `/${relatedTo}/${accountOrPolicyNumber}/contacts`,
                state: {
                    selectTab: 'policyContactsTab',
                },
            },
        },
        accountSourceState: {
            visible: contact === 'accountContacts',
            to: {
                pathname: `/${relatedTo}/${accountOrPolicyNumber}/contacts`,
                state: {
                    selectTab: 'accountContactsTab',
                },
            },
        },
        associatedSourceState: {
            visible: contact === 'associatedContacts',
            to: {
                pathname: `/${relatedTo}/${accountOrPolicyNumber}/contacts`,
                state: {
                    selectTab: 'associatedContactsTab',
                },
            },
        },
        accountContactStatusContainer: {
            visible:
                relatedTo !== 'policies' &&
                !_.get(accountContactDetailsVM, 'active'),
        },
        contactDetailsCompanySection: {
            visible:
                _.get(accountContactDetailsVM, 'subtype') ===
                'Company',
        },
        contactDetailsPersonSection: {
            visible:
                _.get(accountContactDetailsVM, 'subtype') ===
                'Person',
        },
        companyWorkNumberContainer: {
            visible:
                _.get(accountContactDetailsVM, 'workNumber') !==
                undefined && _.get(accountContactDetailsVM, 'workNumber') !== '',
        },
        companyFaxContainer: {
            visible: _.get(accountContactDetailsVM, 'faxNumber') !==
                undefined && _.get(accountContactDetailsVM, 'faxNumber') !== ''
        },
        companyEmailAddressContainer: {
            visible:
                _.get(accountContactDetailsVM, 'emailAddress1') !==
                undefined,
        },
        contactCompanyAddressContainer: {
            visible:
                _.get(
                    accountContactDetailsVM,
                    'primaryAddress.displayName'
                ) !== undefined,
        },
        personDateOfBirthContainer: {
            visible:
                _.get(accountContactDetailsVM, 'dateOfBirth') !==
                undefined,
        },
        personMaritalStatusContainer: {
            visible:
                _.get(accountContactDetailsVM, 'maritalStatus') !==
                undefined,
        },
        personEmailAddressContainer: {
            visible:
                _.get(accountContactDetailsVM, 'emailAddress1') !==
                undefined,
        },
        contactPersonAddressContainer: {
            visible:
                _.get(
                    accountContactDetailsVM,
                    'primaryAddress.displayName'
                ) !== undefined,
        },
        contactRoles: {
            roles: _.get(
                accountContactDetailsVM,
                contact === 'policyContacts'
                    ? 'policyContactRoles'
                    : 'accountContactRoles'
            ),
            associatedContacts: contact,
            relatedRoles:
                contact === 'policyContacts' ? 'policy' : 'account',
            contactType: _.get(accountContactDetailsVM, 'subtype'),
        },
        companyWorkNumber: {
            value: getWorkPhoneNumber(
                _.get(accountContactDetailsVM, 'workNumber')
            ),
        },
        companyFax: {
            value: getWorkPhoneNumber(
                _.get(accountContactDetailsVM, 'faxNumber')
            ),
        },
        contactDisplayName: {
            content: _.get(accountContactDetailsVM, 'displayName'),
        },
        personPhoneNumberContainer: {
            visible: isPrimaryPhoneNumberVisible()
        },
        personPhoneNumberLabel: {
            content: getPhoneLabel(),
        },
        personPhoneNumber: {
            value:getWorkPhoneNumber(
                _.get(accountContactDetailsVM, getPrimaryPhoneNumber()))
        },
        ...getAssociatedAccountsIcon(),
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            link: Link,
        },
    };

    const readValue = (id, path) => {
        return readViewModelValue(
            metadata.pageContent,
            accountContactDetailsVM,
            id,
            path,
            overrideProps
        );
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={accountContactDetailsVM}
            overrideProps={overrideProps}
            resolveValue={readValue}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

CustomContactDetailsPage.propTypes = {
    location: PropTypes.shape({
        state: PropTypes.shape({
            contact: PropTypes.string,
            accountOrPolicyNumber: PropTypes.string,
            relatedTo: PropTypes.string,
            contactId: PropTypes.string,
        }),
    }).isRequired,
    viewModelService: PropTypes.shape({
        create: PropTypes.func,
    }).isRequired,
    authHeader: PropTypes.shape({}),
};

export default withViewModelService(
    withAuthenticationContext(CustomContactDetailsPage)
);
